import * as React from 'react'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSpring, animated, config } from '@react-spring/web'
import { TransitionState } from 'gatsby-plugin-transition-link'

export const PageTransitionSpring = ({
  children,
  extraDuration,
  customAnimation,
  animation,
  delay,
  style,
  disabled,
  trigger,
}) => {
  return (
    <TransitionState>
      {({ mount, current, transitionStatus }) => {
        const disable =
          typeof disabled === 'string'
            ? current &&
              current.state &&
              current.state.nextPath &&
              current.state.nextPath.startsWith(disabled)
            : disabled
        const defaultMs = {
          entering: 400,
          exiting: 400,
          exited: 400,
          entered: 0,
        }

        const d = delay || (current?.delay ? current.delay * 1000 : 0)
        const ms = current ? current.length * 1000 : defaultMs[transitionStatus]

        // Determine if we should show content based on transition status
        const shouldMount =
          transitionStatus === 'entering' || transitionStatus === 'entered'
        return disable ? (
          <div>{children}</div>
        ) : (
          <Ani
            trigger={trigger}
            customAnimation={customAnimation}
            mount={
              typeof mount === 'undefined' && transitionStatus === 'POP'
                ? true
                : typeof mount === 'undefined'
                ? shouldMount
                : mount
            }
            disabled={disabled}
            extraDuration={extraDuration}
            transitionStatus={transitionStatus}
            duration={ms}
            animation={animation}
            delay={d}
            style={style}
          >
            {children}
          </Ani>
        )
      }}
    </TransitionState>
  )
}
PageTransitionSpring.defaultProps = {
  animation: 'fromTop',
  extraDuration: 0,
}
PageTransitionSpring.propTypes = {
  animation: PropTypes.oneOf(['fromTop', 'fromBottom', 'fade', 'custom']),
  children: PropTypes.node,
  customAnimation: PropTypes.object,
  delay: PropTypes.number,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  extraDuration: PropTypes.number,
  style: PropTypes.object,
  trigger: PropTypes.bool,
}

export const Ani = ({
  mount,
  delay,
  animation,
  customAnimation,
  trigger,
  disabled,
  style,
  children,
}) => {
  const [{ value }, api] = useSpring(() => ({
    immediate: false,
    value: 0,
    config: config.slow,
  }))

  useEffect(() => {
    trigger ? api.start({ value: 1 }) : api.start({ value: 0 })
    return () => {
      api.stop()
    }
  }, [trigger, api])

  const ani1 = {
    opacity: value,
    transform: value
      .to({ range: [0, 1], output: [-35, 0] })
      .to((v) => `translateY(${v}px)`),
  }

  const animations = {
    trigger: ani1,
    fromTop: useSpring({
      from: {
        y: -25,
        opacity: 0,
      },
      to: {
        y: mount ? 0 : -25,
        opacity: mount ? 1 : 0,
      },
      delay,
    }),
    fromBottom: useSpring({
      from: {
        y: 15,
        opacity: 0,
      },
      to: {
        y: mount ? 0 : 15,
        opacity: mount ? 1 : 0,
      },
      delay,
    }),
    fade: useSpring({
      from: {
        opacity: 0,
      },
      to: {
        opacity: mount ? 1 : 0,
      },
      delay,
    }),
    custom: useSpring(
      customAnimation
        ? {
            ...customAnimation,
            from: customAnimation.from,
            to: mount ? customAnimation.to : customAnimation.from,
            delay: mount ? delay : 0,
          }
        : {}
    ),
  }

  return disabled ? (
    children
  ) : (
    <animated.div
      style={
        animation === 'custom' || animation === 'trigger'
          ? { ...animations[animation], ...style }
          : {
              opacity: animations[animation].opacity,
              transform:
                animations[animation].y &&
                animations[animation].y.to((y) => `translateY(${y}px)`),
              ...style,
            }
      }
    >
      {children}
    </animated.div>
  )
}

Ani.defaultProps = {
  trigger: true,
}

Ani.propTypes = {
  animation: PropTypes.string,
  children: PropTypes.node,
  customAnimation: PropTypes.shape({
    from: PropTypes.object,
    to: PropTypes.object,
  }),
  delay: PropTypes.number,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  mount: PropTypes.bool,
  style: PropTypes.object,
  trigger: PropTypes.bool,
}
